import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../helpers/AuthContext';
import InvoicesTable from './InvoicesTable';
import CreateInvoiceModal from './CreateInvoiceModal';
import InvoiceImportModal from './InvoiceImportModal';
import InvoiceDetail from './InvoiceDetail';
import { formatCents } from './InvoiceFormParts';
import './Invoices.css';

const STATUS_FILTERS = [
    { value: '', label: 'All' },
    { value: 'draft', label: 'Draft' },
    { value: 'sent', label: 'Sent' },
    { value: 'viewed', label: 'Viewed' },
    { value: 'partially_paid', label: 'Partially Paid' },
    { value: 'pending_settlement', label: 'Pending Settlement' },
    { value: 'paid', label: 'Paid' },
    { value: 'overdue', label: 'Overdue' },
    { value: 'void', label: 'Void' },
    { value: 'canceled', label: 'Canceled' },
];

function InvoicesManager() {
    const { fetch: authFetch, API_URL } = useAuth();
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [statusFilter, setStatusFilter] = useState('');
    const [showCreate, setShowCreate] = useState(false);
    const [showImport, setShowImport] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const LIMIT = 25;

    const loadInvoices = async (pageNum = 1, status = statusFilter) => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: pageNum, limit: LIMIT });
            if (status) params.set('status', status);
            const response = await authFetch(`${API_URL}/v1/seller/invoices?${params}`);
            if (!response.ok) throw new Error('Failed to load invoices');
            const json = await response.json();
            setInvoices(json?.data || []);
            setTotal(json?.meta?.total || 0);
            setPage(pageNum);
        } catch (err) {
            setError('Failed to load invoices. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadInvoices(1, statusFilter);
    }, [statusFilter]);

    const handleViewInvoice = (invoice) => {
        setSelectedInvoice(invoice);
    };

    const handleCloseDetail = () => {
        setSelectedInvoice(null);
        loadInvoices(page);
    };

    const handleCreateSuccess = () => {
        setShowCreate(false);
        loadInvoices(1);
    };

    const handleImportComplete = (summary) => {
        if (summary.successful > 0) {
            loadInvoices(1);
        }
    };

    const visibleOutstandingCents = invoices.reduce((sum, invoice) => sum + (invoice.amountDueCents || 0), 0);
    const paidCount = invoices.filter(invoice => invoice.status === 'paid').length;
    const draftCount = invoices.filter(invoice => invoice.status === 'draft').length;
    const overdueCount = invoices.filter(invoice => invoice.status === 'overdue').length;

    let content;
    if (loading) {
        content = (
            <div className="invoices-state invoices-state--loading">
                <i className="fas fa-spinner fa-spin" aria-hidden="true"></i>
                <span>Loading invoices...</span>
            </div>
        );
    } else if (error) {
        content = <div className="error invoices-state">{error}</div>;
    } else if (invoices.length === 0) {
        content = (
            <div className="invoices-empty">
                <i className="fas fa-file-invoice-dollar" aria-hidden="true"></i>
                <h3>No invoices found</h3>
                {statusFilter ? (
                    <>
                        <p>No invoices match the selected status.</p>
                        <button className="button secondary small" onClick={() => setStatusFilter('')}>
                            Clear filter
                        </button>
                    </>
                ) : (
                    <>
                        <p>Create your first invoice to bill a customer and track payments here.</p>
                        <div className="invoices-actions">
                            <button className="button primary" onClick={() => setShowCreate(true)}>
                                <span className="button-icon" aria-hidden="true">
                                    <i className="fas fa-plus"></i>
                                </span>
                                <span className="button-label">Create invoice</span>
                            </button>
                            <button
                                className="button secondary"
                                onClick={() => setShowImport(true)}
                            >
                                <span className="button-icon" aria-hidden="true">
                                    <i className="fas fa-file-import"></i>
                                </span>
                                <span className="button-label">Import from file</span>
                            </button>
                        </div>
                    </>
                )}
            </div>
        );
    } else {
        content = (
            <InvoicesTable
                invoices={invoices}
                onView={handleViewInvoice}
                onRefresh={() => loadInvoices(page)}
            />
        );
    }

    const totalPages = Math.ceil(total / LIMIT);
    const shouldShowCommandBar = total > 0;
    const commandBarStyle = shouldShowCommandBar ? {} : { display: 'none' };

    return (
        <div className="invoices-manager">
            <div className="invoices-commandbar" style={commandBarStyle}>
                <div>
                    <h3>Invoice list</h3>
                    <p>{total} total invoice{total === 1 ? '' : 's'}{statusFilter ? ` filtered by ${statusFilter.replace('_', ' ')}` : ''}</p>
                </div>
                <div className="invoices-commandbar__actions">
                    <div className="invoices-actions">
                        <button
                            className="button secondary"
                            onClick={() => setShowImport(true)}
                        >
                            <span className="button-icon" aria-hidden="true">
                                <i className="fas fa-file-import"></i>
                            </span>
                            <span className="button-label">Import from file</span>
                        </button>
                        <button
                            className="button primary"
                            onClick={() => setShowCreate(true)}
                        >
                            <span className="button-icon" aria-hidden="true">
                                <i className="fas fa-plus"></i>
                            </span>
                            <span className="button-label">Create invoice</span>
                        </button>
                    </div>
                </div>
            </div>

            <div className="invoices-summary-grid" style={{display: 'none'}}>
                <div className="invoices-summary-card">
                    <span>Outstanding shown</span>
                    <strong>{formatCents(visibleOutstandingCents, invoices[0]?.currency || 'USD')}</strong>
                </div>
                <div className="invoices-summary-card">
                    <span>Drafts shown</span>
                    <strong>{draftCount}</strong>
                </div>
                <div className="invoices-summary-card">
                    <span>Paid shown</span>
                    <strong>{paidCount}</strong>
                </div>
                <div className="invoices-summary-card invoices-summary-card--attention">
                    <span>Overdue shown</span>
                    <strong>{overdueCount}</strong>
                </div>
            </div>

            <div className="invoices-list-panel">
                <div className="invoices-toolbar" style={{display: "none"}}>
                    <div className="group invoices-filters">
                        <label htmlFor="invoice-status-filter">Status</label>
                        <select
                            id="invoice-status-filter"
                            value={statusFilter}
                            onChange={e => setStatusFilter(e.target.value)}
                        >
                            {STATUS_FILTERS.map(f => (
                                <option key={f.value} value={f.value}>{f.label}</option>
                            ))}
                        </select>
                    </div>
                    <button
                        className="button secondary small"
                        onClick={() => loadInvoices(page)}
                        disabled={loading}
                    >
                        <i className="fas fa-sync-alt" aria-hidden="true"></i>
                        Refresh
                    </button>
                </div>

                {content}
            </div>

            {totalPages > 1 && (
                <div className="invoices-pagination">
                    <button
                        disabled={page <= 1}
                        onClick={() => loadInvoices(page - 1)}
                        className="button secondary small"
                    >
                        Previous
                    </button>
                    <span>Page {page} of {totalPages} ({total} total)</span>
                    <button
                        disabled={page >= totalPages}
                        onClick={() => loadInvoices(page + 1)}
                        className="button secondary small"
                    >
                        Next
                    </button>
                </div>
            )}

            {showCreate && (
                <CreateInvoiceModal
                    onClose={() => setShowCreate(false)}
                    onSuccess={handleCreateSuccess}
                />
            )}

            {showImport && (
                <InvoiceImportModal
                    onClose={() => setShowImport(false)}
                    onComplete={handleImportComplete}
                />
            )}

            {selectedInvoice && (
                <InvoiceDetail
                    invoiceId={selectedInvoice.uuid}
                    onClose={handleCloseDetail}
                />
            )}
        </div>
    );
}

export default InvoicesManager;
